/**
 * Tenant-scoped query & mutation wrappers.
 *
 * Every company-scoped call used to hand-roll the same skeleton: acquire the
 * company client via {@link useCompanyApi}, derive the active org id from
 * {@link useAuth} for the cache key, guard the query/mutation fn with
 * `if (!company) throw new Error("company unavailable")`, and gate `enabled` on
 * the client being present. These two hooks encapsulate that skeleton so a
 * caller writes only the parts that actually differ.
 *
 * The org-id-first cache policy (see {@link file://../lib/query-keys.ts}) is
 * baked in: `key` is a builder that RECEIVES the active org id and returns a
 * key — pass a factory from `queryKeys` and the org segment is supplied for
 * you, so a tenant key can never silently forget its org scope.
 */

import {
  type QueryKey,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
  useMutation,
  useQuery,
} from "@tanstack/react-query"
import { useAuth } from "./useAuth.ts"
import { type CompanyApi, useCompanyApi } from "./useCompanyApi.ts"

/** The active org id — `null` until the session hydrates. Same value the cache keys embed. */
type TOrgId = string | null

/** Everything a tenant query needs beyond the standard company/org/guard skeleton. */
interface ICompanyQueryOptions<TQueryFnData, TError, TData>
  extends Omit<UseQueryOptions<TQueryFnData, TError, TData, QueryKey>, "queryKey" | "queryFn" | "enabled"> {
  /** Cache-key builder — receives the active org id so the org-id-first policy is automatic. */
  key: (orgId: TOrgId) => QueryKey
  /** Runs only when a company client exists; receives the guaranteed-non-null client. */
  queryFn: (company: CompanyApi) => TQueryFnData | Promise<TQueryFnData>
  /** Extra gate ANDed with the company-present guard. Defaults to always-on. */
  enabled?: boolean
}

/**
 * A tenant-scoped `useQuery`. Supplies the org id to `key`, runs `queryFn` with
 * the resolved company client, and gates `enabled` on that client existing
 * (composed with any caller-supplied `enabled`). All other query options
 * (`staleTime`, `refetchInterval`, `retry`, `select`, …) pass straight through.
 */
export function useCompanyQuery<TQueryFnData, TError = Error, TData = TQueryFnData>(
  options: ICompanyQueryOptions<TQueryFnData, TError, TData>,
): UseQueryResult<TData, TError> {
  const { key, queryFn, enabled = true, ...rest } = options
  const company = useCompanyApi()
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null

  return useQuery<TQueryFnData, TError, TData, QueryKey>({
    ...rest,
    queryKey: key(orgId),
    queryFn: () => {
      if (!company) throw new Error("company unavailable")
      return queryFn(company)
    },
    enabled: enabled && !!company,
  })
}

/** Everything a tenant mutation needs beyond the company/guard skeleton. */
interface ICompanyMutationOptions<TData, TError, TVariables, TContext>
  extends Omit<UseMutationOptions<TData, TError, TVariables, TContext>, "mutationFn"> {
  /** Receives the guaranteed-non-null company client plus the caller's variables. */
  mutationFn: (company: CompanyApi, variables: TVariables) => Promise<TData>
}

/**
 * A tenant-scoped `useMutation`. Runs `mutationFn` with the resolved company
 * client, throwing the same `"company unavailable"` error the hand-rolled sites
 * did when there is no active company. `onSuccess`/`onError`/`onSettled` and
 * every other mutation option pass straight through untouched.
 */
export function useCompanyMutation<TData = unknown, TVariables = void, TError = Error, TContext = unknown>(
  options: ICompanyMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> {
  const { mutationFn, ...rest } = options
  const company = useCompanyApi()

  return useMutation<TData, TError, TVariables, TContext>({
    ...rest,
    mutationFn: (variables: TVariables) => {
      if (!company) throw new Error("company unavailable")
      return mutationFn(company, variables)
    },
  })
}
